import React, { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { EditableItemList } from '@/components/EditableItemList';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ChefHat, Camera, Loader2, Sparkles, Plus, Minus, Check } from 'lucide-react';
import axios from 'axios';

const API = '/api';

export const MealPrepTab = ({ onBatchCreated }) => {
  const { authHeaders, token } = useAuth();
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [phase, setPhase] = useState('input'); // input | analyzing | result
  const [items, setItems] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [servingCount, setServingCount] = useState(4);
  const [mealType, setMealType] = useState('lunch');
  const [error, setError] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!imageFile) return;
    setPhase('analyzing');
    const formData = new FormData();
    formData.append('file', imageFile);
    try {
      const res = await axios.post(`${API}/meals/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        
      });
      const analysis = res.data.initial_analysis || res.data.final_analysis;
      setItems(analysis?.items || []);
      setBatchName(analysis?.items?.[0]?.name ? `${analysis.items[0].name} Prep` : 'Meal Prep');
      setPhase('result');
      // Delete the temp meal since we're creating a batch
      try { await axios.delete(`${API}/meals/${res.data.meal_id}`, authHeaders()); } catch {}
    } catch (err) {
      setError('Analysis failed. Try again.');
      setPhase('input');
    }
  };

  const handleCreateBatch = async () => {
    const totalMacros = items.reduce((acc, i) => ({
      calories: acc.calories + (i.calories || 0),
      protein: acc.protein + (i.protein || 0),
      carbs: acc.carbs + (i.carbs || 0),
      fat: acc.fat + (i.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

    try {
      const res = await axios.post(`${API}/batch-meals`, {
        name: batchName,
        serving_count: servingCount,
        total_macros: totalMacros,
        items,
        meal_type: mealType,
      }, authHeaders());
      onBatchCreated(res.data);
    } catch (err) {
      setError('Failed to create batch.');
    }
  };

  const totalMacros = items.reduce((acc, i) => ({
    calories: acc.calories + (i.calories || 0),
    protein: acc.protein + (i.protein || 0),
    carbs: acc.carbs + (i.carbs || 0),
    fat: acc.fat + (i.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const perServing = {
    calories: Math.round(totalMacros.calories / servingCount),
    protein: Math.round(totalMacros.protein / servingCount * 10) / 10,
    carbs: Math.round(totalMacros.carbs / servingCount * 10) / 10,
    fat: Math.round(totalMacros.fat / servingCount * 10) / 10,
  };

  if (phase === 'analyzing') {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#1A3A4A] dark:text-[#6B9E8A] animate-spin mb-3" />
        <p className="font-medium text-[#1A3A4A] dark:text-[#F0EDE8]">Analyzing batch...</p>
      </div>
    );
  }

  if (phase === 'result') {
    return (
      <div className="animate-slide-up-spring" data-testid="batch-result">
        {/* Batch Name */}
        <div className="mb-4">
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-1.5 block">Batch Name</label>
          <input
            data-testid="batch-name-input"
            value={batchName}
            onChange={e => setBatchName(e.target.value)}
            className="w-full h-12 px-4 rounded-xl bg-white dark:bg-[#1E2A32] border border-[#1A3A4A]/10 dark:border-white/10 text-[#1A3A4A] dark:text-[#F0EDE8] text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#1A3A4A]/20"
          />
        </div>

        {/* Serving Count */}
        <div className="rounded-[16px] border backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] bg-white/[0.85] border-white/30 dark:bg-[#141C22]/[0.85] dark:border-white/10 p-5 mb-4">
          <label className="text-sm font-medium text-[#2D2D2D] dark:text-[#F0EDE8] mb-3 block">How many servings?</label>
          <div className="flex items-center justify-center gap-6">
            <button data-testid="serving-minus" onClick={() => setServingCount(Math.max(1, servingCount - 1))} className="w-12 h-12 rounded-xl bg-[#1A3A4A]/5 dark:bg-white/5 flex items-center justify-center">
              <Minus className="w-5 h-5 text-[#1A3A4A] dark:text-[#F0EDE8]" />
            </button>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#1A3A4A] dark:text-[#F0EDE8]">{servingCount}</div>
              <div className="text-xs text-[#7A7A72]">servings</div>
            </div>
            <button data-testid="serving-plus" onClick={() => setServingCount(Math.min(20, servingCount + 1))} className="w-12 h-12 rounded-xl bg-[#1A3A4A]/5 dark:bg-white/5 flex items-center justify-center">
              <Plus className="w-5 h-5 text-[#1A3A4A] dark:text-[#F0EDE8]" />
            </button>
          </div>
        </div>

        {/* Per Serving Macros */}
        <div className="rounded-[16px] border backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] bg-white/[0.85] border-white/30 dark:bg-[#141C22]/[0.85] dark:border-white/10 p-5 mb-4">
          <h3 className="text-xs font-semibold text-[#7A7A72] uppercase tracking-wider mb-3">Per Serving</h3>
          <div className="text-center mb-3">
            <span className="text-3xl font-bold text-[#1A3A4A] dark:text-[#F0EDE8]">{perServing.calories}</span>
            <span className="text-sm text-[#7A7A72] ml-1">cal</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><span className="text-lg font-bold text-[#4A90D9]">{perServing.protein}g</span><div className="text-xs text-[#7A7A72]">Protein</div></div>
            <div><span className="text-lg font-bold text-[#E5A84B]">{perServing.carbs}g</span><div className="text-xs text-[#7A7A72]">Carbs</div></div>
            <div><span className="text-lg font-bold text-[#D4736A]">{perServing.fat}g</span><div className="text-xs text-[#7A7A72]">Fat</div></div>
          </div>
        </div>

        {/* Items */}
        <div className="rounded-[16px] border backdrop-blur-[20px] [-webkit-backdrop-filter:blur(20px)] bg-white/[0.85] border-white/30 dark:bg-[#141C22]/[0.85] dark:border-white/10 p-5 mb-4">
          <h3 className="text-xs font-semibold text-[#7A7A72] uppercase tracking-wider mb-3">Batch Ingredients</h3>
          <EditableItemList items={items} onItemsChange={setItems} />
        </div>

        {error && <p className="text-sm text-[#C45B5B] mb-3">{error}</p>}

        <Button data-testid="create-batch-btn" onClick={handleCreateBatch} className="w-full h-14 rounded-xl bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white font-semibold text-base">
          <Check className="w-5 h-5 mr-2" /> Create Batch & Log 1 Serving
        </Button>
      </div>
    );
  }

  // Input phase
  return (
    <div className="animate-fade-in-up" data-testid="meal-prep-tab">
      {!imagePreview ? (
        <div onClick={() => fileInputRef.current?.click()} className="aspect-[4/3] rounded-2xl border-2 border-dashed border-[#1A3A4A]/15 dark:border-white/15 flex flex-col items-center justify-center cursor-pointer hover:border-[#1A3A4A]/30 bg-white dark:bg-[#1E2A32]">
          <div className="w-16 h-16 rounded-2xl bg-[#6B9E8A]/10 flex items-center justify-center mb-4">
            <ChefHat className="w-8 h-8 text-[#6B9E8A]" />
          </div>
          <p className="font-medium text-[#1A3A4A] dark:text-[#F0EDE8]">Photograph your batch</p>
          <p className="text-sm text-[#7A7A72] mt-1">Snap the full pot, pan, or container</p>
        </div>
      ) : (
        <div className="relative mb-4">
          <img src={imagePreview} alt="Batch" className="w-full aspect-[4/3] object-cover rounded-2xl" />
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
      {error && <p className="text-sm text-[#C45B5B] mt-3">{error}</p>}
      {imagePreview && (
        <Button data-testid="analyze-batch-btn" onClick={handleAnalyze} className="w-full h-14 rounded-xl bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white font-semibold text-base mt-4">
          <Sparkles className="w-5 h-5 mr-2" /> Analyze Batch
        </Button>
      )}
    </div>
  );
};
